import { useCallback, useEffect, useMemo, useState } from "react";

import {
  getCustomers,
  restoreCustomerAccurateCoordinates,
  triggerCustomerSync,
  updateCustomerCoordinates,
} from "../../api/client.js";

import { Dialog } from "../Dialog.jsx";
import { Icon } from "../Icon.jsx";
import { SortableHeader } from "../SortableHeader.jsx";
import { useManualSync } from "../../hooks/useManualSync.js";
import { getNextTableSort, useTableSort } from "../../hooks/useTableSort.js";
import {
  DataMasterActiveFilters,
  DataMasterKpiCard,
  DataMasterKpiStrip,
  DataMasterPagination,
  DataMasterSection,
  DataMasterTableShell,
  DataMasterToolbar,
  SyncStatusStrip,
} from "./DataMasterLayout.jsx";

const EMPTY_COORDINATE_FORM = {
  latitude: "",
  longitude: "",
};

const CUSTOMER_COORDINATE_STATUS_LABELS = {
  VALID: "Valid",
  REVIEW: "Perlu diperiksa",
  MISSING: "Belum tersedia",
};

const CUSTOMER_COORDINATE_SOURCE_LABELS = {
  ACCURATE: "Accurate",
  MANUAL: "Manual",
  MISSING: "Tanpa sumber",
};

const numberFormatter = new Intl.NumberFormat("id-ID");

function createCustomerFilters(search = "") {
  return {
    search,
    coordinateStatus: "",
    coordinateSource: "",
    billingCity: "",
    province: "",
  };
}

function latestSyncResult(syncRun) {
  if (!syncRun) return {};
  if (syncRun.result) return syncRun.result;

  try {
    return syncRun.result_json ? JSON.parse(syncRun.result_json) : {};
  } catch {
    return {};
  }
}

function syncStatusLabel(syncRun) {
  const status = syncRun?.status;
  if (["QUEUED", "RUNNING"].includes(status)) return "Sinkronisasi sedang berjalan";
  if (status === "SUCCESS") return "Sinkronisasi berhasil";
  if (status === "PARTIAL_SUCCESS") return "Sinkronisasi selesai sebagian";
  if (status === "FAILED") return "Sinkronisasi gagal";
  return "Belum pernah sinkron";
}

function customerSyncMetrics(syncRun) {
  const result = latestSyncResult(syncRun);
  const errors = Array.isArray(result.errors)
    ? result.errors.length
    : Number(syncRun?.error_count || 0);

  return [
    {
      label: "pelanggan",
      value: numberFormatter.format(
        Number(result.rowsUpserted ?? syncRun?.records_upserted ?? 0),
      ),
    },
    {
      label: "halaman",
      value: numberFormatter.format(
        Number(result.pagesProcessed ?? syncRun?.pages_processed ?? 0),
      ),
    },
    { label: "error", value: numberFormatter.format(errors) },
  ];
}

function formatTimestamp(value) {
  if (!value) return "Belum pernah";

  const parsed = new Date(value);

  if (Number.isNaN(parsed.getTime())) {
    return "Belum pernah";
  }

  return new Intl.DateTimeFormat("id-ID", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(parsed);
}

function formatCoordinate(value) {
  if (
    value === null ||
    value === undefined ||
    value === ""
  ) {
    return "—";
  }

  const numeric = Number(value);

  if (!Number.isFinite(numeric)) {
    return "—";
  }

  return numeric
    .toFixed(7)
    .replace(/0+$/, "")
    .replace(/\.$/, "");
}

function hasCoordinatePair(latitude, longitude) {
  const lat = Number(latitude);
  const lng = Number(longitude);

  return (
    latitude !== null &&
    latitude !== undefined &&
    latitude !== "" &&
    longitude !== null &&
    longitude !== undefined &&
    longitude !== "" &&
    Number.isFinite(lat) &&
    Number.isFinite(lng) &&
    lat >= -90 &&
    lat <= 90 &&
    lng >= -180 &&
    lng <= 180
  );
}

function normalizeCoordinateStatus(
  status,
  latitude,
  longitude,
) {
  if (hasCoordinatePair(latitude, longitude)) {
    return "VALID";
  }

  const normalized = String(status || "MISSING").toUpperCase();

  return [
    "NEEDS_REVIEW",
    "SUSPECTED_SWAPPED",
    "OUT_OF_RANGE",
    "INCOMPLETE",
    "MISSING",
  ].includes(normalized)
    ? normalized
    : "MISSING";
}

function coordinateStatusMessage(status) {
  const normalized = String(status || "MISSING").toUpperCase();

  return {
    SUSPECTED_SWAPPED:
      "Latitude dan longitude dari Accurate diduga tertukar. Periksa Accurate atau simpan koreksi manual.",
    OUT_OF_RANGE:
      "Koordinat dari Accurate berada di luar rentang valid. Periksa Accurate atau simpan koreksi manual.",
    INCOMPLETE:
      "Koordinat dari Accurate belum lengkap. Latitude dan longitude harus tersedia bersamaan.",
    NEEDS_REVIEW:
      "Koordinat perlu diperiksa sebelum digunakan untuk peta dan jalur.",
    MISSING:
      "Koordinat belum tersedia.",
    VALID:
      "Koordinat valid untuk operasional.",
  }[normalized] || "Koordinat belum tersedia.";
}

function CoordinateStatusBadge({
  status,
  latitude,
  longitude,
}) {
  const normalized = normalizeCoordinateStatus(
    status,
    latitude,
    longitude,
  );

  const labels = {
    VALID: "Valid",
    NEEDS_REVIEW: "Perlu diperiksa",
    SUSPECTED_SWAPPED: "Diduga tertukar",
    OUT_OF_RANGE: "Di luar rentang",
    INCOMPLETE: "Belum lengkap",
    MISSING: "Belum tersedia",
  };

  return (
    <span
      className={`coordinate-status coordinate-status--${normalized
        .toLowerCase()
        .replaceAll("_", "-")}`}
    >
      {labels[normalized]}
    </span>
  );
}

function getCoordinateSourceKind(customer) {
  const source = String(
    customer?.coordinateDisplaySource ||
      customer?.coordinateSource ||
      "",
  ).toUpperCase();

  if (source === "ACCURATE") {
    return "ACCURATE";
  }

  if (
    source ||
    customer?.isManualCoordinateOverride
  ) {
    return "MANUAL";
  }

  return "MISSING";
}

function CoordinateSourceBadge({ customer }) {
  const source = getCoordinateSourceKind(customer);

  const labels = {
    ACCURATE: "Accurate",
    MANUAL: "Manual",
    MISSING: "—",
  };

  return (
    <span
      className={[
        "coordinate-source",
        `coordinate-source--${source.toLowerCase()}`,
      ].join(" ")}
    >
      {labels[source]}
    </span>
  );
}

function mapCustomerToCoordinateForm(customer) {
  const hasActiveCoordinates = hasCoordinatePair(
    customer.latitude,
    customer.longitude,
  );
  const latitude = hasActiveCoordinates
    ? customer.latitude
    : customer.accurateLatitude;
  const longitude = hasActiveCoordinates
    ? customer.longitude
    : customer.accurateLongitude;

  return {
    latitude:
      latitude === null || latitude === undefined
        ? ""
        : String(latitude),
    longitude:
      longitude === null || longitude === undefined
        ? ""
        : String(longitude),
  };
}

function validateCoordinateForm(form) {
  const latitudeFilled =
    String(form.latitude ?? "").trim() !== "";

  const longitudeFilled =
    String(form.longitude ?? "").trim() !== "";

  if (latitudeFilled !== longitudeFilled) {
    return "Latitude dan longitude wajib diisi bersamaan.";
  }

  if (!latitudeFilled) {
    return "";
  }

  const latitude = Number(
    String(form.latitude).replace(",", "."),
  );

  const longitude = Number(
    String(form.longitude).replace(",", "."),
  );

  if (
    !Number.isFinite(latitude) ||
    latitude < -90 ||
    latitude > 90
  ) {
    return "Latitude harus berupa angka antara -90 sampai 90.";
  }

  if (
    !Number.isFinite(longitude) ||
    longitude < -180 ||
    longitude > 180
  ) {
    return "Longitude harus berupa angka antara -180 sampai 180.";
  }

  return "";
}

export function CustomersTab({ initialSearch = "" }) {
  const initialFilters = useMemo(
    () => createCustomerFilters(initialSearch),
    [initialSearch],
  );
  const [customers, setCustomers] = useState([]);
  const [summary, setSummary] = useState({});
  const [filterOptions, setFilterOptions] = useState({
    billingCities: [],
    provinces: [],
  });
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 50,
    pageCount: 1,
    rowCount: 0,
  });
  const [draftFilters, setDraftFilters] = useState(initialFilters);
  const [filters, setFilters] = useState(initialFilters);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [coordinateCustomer, setCoordinateCustomer] = useState(null);
  const [coordinateForm, setCoordinateForm] = useState(EMPTY_COORDINATE_FORM);
  const [coordinateAction, setCoordinateAction] = useState("");
  const { sort, setSort } = useTableSort("customerName");

  const load = useCallback(
    async (
      page = 1,
      nextFilters = filters,
      nextSort = sort,
      nextPageSize = pagination.pageSize,
    ) => {
      setLoading(true);
      setError("");

      try {
        const payload = await getCustomers({
          page,
          pageSize: nextPageSize,
          ...nextFilters,
          sortBy: nextSort.key,
          sortDir: nextSort.direction,
        });

        setCustomers(payload.customers || []);
        setSummary(payload.summary || {});
        setFilterOptions(
          payload.filterOptions || { billingCities: [], provinces: [] },
        );
        setPagination(
          payload.pagination || {
            page,
            pageSize: nextPageSize,
            pageCount: 1,
            rowCount: 0,
          },
        );
        sync.setSyncRun(payload.latestSync);
      } catch (requestError) {
        setError(requestError.message);
      } finally {
        setLoading(false);
      }
    },
    [filters, pagination.pageSize, sort],
  );

  const sync = useManualSync(triggerCustomerSync, async () => {
    await load(1, filters, sort);
  });

  useEffect(() => {
    setDraftFilters(initialFilters);
    setFilters(initialFilters);
    load(1, initialFilters);
  }, [initialSearch]);

  function updateDraftFilter(key, value) {
    setDraftFilters((current) => ({ ...current, [key]: value }));
  }

  function submitFilters(event) {
    event.preventDefault();
    const nextFilters = {
      ...draftFilters,
      search: draftFilters.search.trim(),
    };
    setFilters(nextFilters);
    load(1, nextFilters, sort);
  }

  function clearFilters() {
    const nextFilters = createCustomerFilters();
    setDraftFilters(nextFilters);
    setFilters(nextFilters);
    load(1, nextFilters, sort);
  }

  function toggleSort(column) {
    const nextSort = getNextTableSort(sort, column);
    setSort(nextSort);
    load(1, filters, nextSort);
  }

  function changePageSize(nextPageSize) {
    load(1, filters, sort, nextPageSize);
  }

  function openCoordinateEditor(
    customer,
  ) {
    setCoordinateCustomer(customer);

    setCoordinateForm(
      mapCustomerToCoordinateForm(
        customer,
      ),
    );

    setMessage("");
    setError("");
  }

  function closeCoordinateEditor() {
    if (coordinateAction) return;

    setCoordinateCustomer(null);

    setCoordinateForm(
      EMPTY_COORDINATE_FORM,
    );
  }

  function clearCoordinateForm() {
    setCoordinateForm(
      EMPTY_COORDINATE_FORM,
    );
  }

  async function saveCoordinates(
    event,
  ) {
    event.preventDefault();

    const validationError =
      validateCoordinateForm(
        coordinateForm,
      );

    if (validationError) {
      setError(validationError);
      return;
    }

    const latitudeText = String(
      coordinateForm.latitude ?? "",
    ).trim();

    const longitudeText = String(
      coordinateForm.longitude ?? "",
    ).trim();

    const hasCoordinates = Boolean(
      latitudeText &&
        longitudeText,
    );

    setCoordinateAction("save");
    setMessage("");
    setError("");

    try {
      const response =
        await updateCustomerCoordinates(
          coordinateCustomer.id,

          hasCoordinates
            ? {
                latitude:
                  latitudeText,

                longitude:
                  longitudeText,
              }
            : {
                latitude: null,
                longitude: null,
              },
        );

      setMessage(
        response.message ||
          "Override koordinat manual berhasil disimpan.",
      );

      setCoordinateCustomer(null);

      setCoordinateForm(
        EMPTY_COORDINATE_FORM,
      );

      await load(
        pagination.page,
        filters,
        sort,
      );
    } catch (requestError) {
      setError(
        requestError.message,
      );
    } finally {
      setCoordinateAction("");
    }
  }

  async function restoreAccurateCoordinates() {
    if (
      !coordinateCustomer ||
      coordinateAction
    ) {
      return;
    }

    setCoordinateAction("restore");
    setMessage("");
    setError("");

    try {
      const response =
        await restoreCustomerAccurateCoordinates(
          coordinateCustomer.id,
        );

      setMessage(
        response.message ||
          "Koordinat berhasil dikembalikan ke baseline Accurate.",
      );

      setCoordinateCustomer(null);

      setCoordinateForm(
        EMPTY_COORDINATE_FORM,
      );

      await load(
        pagination.page,
        filters,
        sort,
      );
    } catch (requestError) {
      setError(
        requestError.message,
      );
    } finally {
      setCoordinateAction("");
    }
  }

  return (
    <DataMasterSection
      error={error || sync.syncError}
      message={message}
      subtitle="Kelola data pelanggan Accurate, kualitas koordinat, dan koreksi manual untuk kebutuhan operasional."
      title="Pelanggan"
    >
      <DataMasterKpiStrip label="Ringkasan kualitas data pelanggan">
        <DataMasterKpiCard
          helper="Sesuai filter aktif"
          icon="user"
          label="Total pelanggan"
          value={summary.total}
        />
        <DataMasterKpiCard
          helper="Siap digunakan di peta"
          icon="check"
          label="Koordinat valid"
          tone="success"
          value={summary.valid}
        />
        <DataMasterKpiCard
          helper="Override operasional"
          icon="location"
          label="Koreksi manual"
          tone="info"
          value={summary.manual}
        />
        <DataMasterKpiCard
          helper="Perlu verifikasi"
          icon="warning"
          label="Perlu diperiksa"
          tone="warning"
          value={summary.review}
        />
        <DataMasterKpiCard
          helper="Belum dapat dipetakan"
          icon="info"
          label="Belum tersedia"
          tone="danger"
          value={summary.missing}
        />
      </DataMasterKpiStrip>

      <SyncStatusStrip
        actions={
          <button
            className="primary-button"
            disabled={sync.syncing}
            onClick={sync.startSync}
            type="button"
          >
            <span className={sync.syncing ? "is-spinning" : ""}>
              <Icon name="refresh" size={15} />
            </span>
            {sync.syncing ? "Menyinkronkan…" : "Sinkronkan Pelanggan"}
          </button>
        }
        lastSynced={formatTimestamp(
          sync.syncRun?.completed_at ||
            sync.syncRun?.updated_at ||
            sync.syncRun?.created_at,
        )}
        metrics={customerSyncMetrics(sync.syncRun)}
        progress={
          ["QUEUED", "RUNNING"].includes(sync.syncRun?.status)
            ? `${numberFormatter.format(
                Number(sync.syncRun?.records_seen || 0),
              )} data telah dibaca`
            : ""
        }
        status={syncStatusLabel(sync.syncRun)}
        title="Sumber: Accurate · Koordinat dapat dikoreksi manual"
      />

      <DataMasterToolbar className="dm-toolbar--customers" onSubmit={submitFilters}>
        <Icon name="search" size={15} />
        <input
          aria-label="Cari pelanggan"
          onChange={(event) => updateDraftFilter("search", event.target.value)}
          placeholder="Cari nomor, pelanggan, area, provinsi, atau sales"
          value={draftFilters.search}
        />
        <select
          aria-label="Status koordinat"
          onChange={(event) =>
            updateDraftFilter("coordinateStatus", event.target.value)
          }
          value={draftFilters.coordinateStatus}
        >
          <option value="">Semua status koordinat</option>
          {Object.entries(CUSTOMER_COORDINATE_STATUS_LABELS).map(
            ([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ),
          )}
        </select>
        <select
          aria-label="Sumber koordinat"
          onChange={(event) =>
            updateDraftFilter("coordinateSource", event.target.value)
          }
          value={draftFilters.coordinateSource}
        >
          <option value="">Semua sumber koordinat</option>
          {Object.entries(CUSTOMER_COORDINATE_SOURCE_LABELS).map(
            ([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ),
          )}
        </select>
        <select
          aria-label="Area pelanggan"
          onChange={(event) => updateDraftFilter("billingCity", event.target.value)}
          value={draftFilters.billingCity}
        >
          <option value="">Semua area</option>
          {(filterOptions.billingCities || []).map((value) => (
            <option key={value} value={value}>
              {value}
            </option>
          ))}
        </select>
        <select
          aria-label="Provinsi pelanggan"
          onChange={(event) => updateDraftFilter("province", event.target.value)}
          value={draftFilters.province}
        >
          <option value="">Semua provinsi</option>
          {(filterOptions.provinces || []).map((value) => (
            <option key={value} value={value}>
              {value}
            </option>
          ))}
        </select>
        <button className="primary-button" disabled={loading} type="submit">
          <Icon name="filter" size={14} />
          Terapkan
        </button>
        <button className="text-button" disabled={loading} onClick={clearFilters} type="button">
          Bersihkan
        </button>
      </DataMasterToolbar>

      <DataMasterActiveFilters
        filters={[
          { label: "Pencarian", value: filters.search },
          {
            label: "Status",
            value: CUSTOMER_COORDINATE_STATUS_LABELS[filters.coordinateStatus],
          },
          {
            label: "Sumber",
            value: CUSTOMER_COORDINATE_SOURCE_LABELS[filters.coordinateSource],
          },
          { label: "Area", value: filters.billingCity },
          { label: "Provinsi", value: filters.province },
        ]}
        onClear={clearFilters}
      />

      <DataMasterTableShell
        className="dm-table-shell--customers dm-table-shell--accurate"
        emptyText="Tidak ada pelanggan yang sesuai dengan filter aktif."
        loading={loading}
        rowCount={customers.length}
      >
        <table className="master-table master-table--customers master-table--customer-coordinates dm-table--fixed">
          <colgroup>
            <col className="dm-col-customer" />
            <col className="dm-col-region" />
            <col className="dm-col-name" />
            <col className="dm-col-coordinate" />
            <col className="dm-col-status-wide" />
            <col className="dm-col-status" />
            <col className="dm-col-operational" />
            <col className="dm-col-date" />
            <col className="dm-col-action" />
          </colgroup>
          <thead>
            <tr>
              <SortableHeader column="customerName" sort={sort} toggleSort={toggleSort}>
                Pelanggan
              </SortableHeader>
              <SortableHeader column="billingCity" sort={sort} toggleSort={toggleSort}>
                Wilayah
              </SortableHeader>
              <SortableHeader column="salesName" sort={sort} toggleSort={toggleSort}>
                Sales
              </SortableHeader>
              <th>Koordinat</th>
              <SortableHeader column="coordinateStatus" sort={sort} toggleSort={toggleSort}>
                Status koordinat
              </SortableHeader>
              <SortableHeader column="coordinateDisplaySource" sort={sort} toggleSort={toggleSort}>
                Sumber
              </SortableHeader>
              <th>Operasional</th>
              <SortableHeader column="lastSyncedAt" sort={sort} toggleSort={toggleSort}>
                Terakhir sinkron
              </SortableHeader>
              <th className="sticky-action-cell master-action-cell master-action-cell--wide">
                Aksi
              </th>
            </tr>
          </thead>
          <tbody>
            {!loading &&
              customers.map((customer) => {
                const coordinateStatus = normalizeCoordinateStatus(
                  customer.displayCoordinateStatus || customer.coordinateStatus,
                  customer.displayLatitude,
                  customer.displayLongitude,
                );

                return (
                  <tr
                    className={coordinateStatus === "VALID" ? "" : "coordinate-review-row"}
                    key={customer.id}
                  >
                    <td>
                      <div className="dm-cell-stack dm-cell-stack--primary">
                        <strong>{customer.customerName}</strong>
                        <span>{customer.customerNo || "—"}</span>
                      </div>
                    </td>
                    <td>
                      <div className="dm-cell-stack">
                        <strong>{customer.billingCity || "—"}</strong>
                        <span>{customer.province || "Provinsi belum tersedia"}</span>
                      </div>
                    </td>
                    <td>{customer.salesName || "—"}</td>
                    <td>
                      <div className="dm-cell-stack dm-cell-stack--coordinate">
                        <strong>
                          {formatCoordinate(
                            customer.displayLatitude ?? customer.latitude,
                          )}
                        </strong>
                        <span>
                          {formatCoordinate(
                            customer.displayLongitude ?? customer.longitude,
                          )}
                        </span>
                      </div>
                    </td>
                    <td>
                      <div className="coordinate-status-cell">
                        <CoordinateStatusBadge
                          latitude={customer.displayLatitude ?? customer.latitude}
                          longitude={customer.displayLongitude ?? customer.longitude}
                          status={customer.displayCoordinateStatus || customer.coordinateStatus}
                        />
                        {coordinateStatus !== "VALID" && (
                          <small className="coordinate-status-cell__warning">
                            {customer.coordinateWarningMessage ||
                              coordinateStatusMessage(
                                customer.displayCoordinateStatus || customer.coordinateStatus,
                              )}
                          </small>
                        )}
                      </div>
                    </td>
                    <td>
                      <CoordinateSourceBadge customer={customer} />
                    </td>
                    <td>
                      <div className="dm-cell-stack">
                        <strong>{customer.access || "Akses belum tersedia"}</strong>
                        <span>Jam tutup: {customer.closeTime || "—"}</span>
                      </div>
                    </td>
                    <td>{formatTimestamp(customer.lastSyncedAt)}</td>
                    <td className="sticky-action-cell master-action-cell master-action-cell--wide">
                      <div className="master-actions master-actions--single">
                        <button
                          className="master-action-button master-action-button--coordinate"
                          onClick={() => openCoordinateEditor(customer)}
                          type="button"
                        >
                          {coordinateStatus === "VALID"
                            ? "Edit Koordinat"
                            : "Perbaiki Koordinat"}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </DataMasterTableShell>

      <DataMasterPagination
        loading={loading}
        onPageChange={(page) => load(page, filters, sort)}
        onPageSizeChange={changePageSize}
        pagination={pagination}
      />

      {coordinateCustomer && (
        <Dialog
          onClose={
            closeCoordinateEditor
          }
          title={`Koordinat ${coordinateCustomer.customerName}`}
        >
          <form
            className="dialog-form"
            onSubmit={
              saveCoordinates
            }
          >
            <div className="dialog-card__body grouped-master-form">
              <section className="master-form-group">
                <div className="coordinate-editor-summary">
                  <div>
                    <span>
                      No. Pelanggan
                    </span>

                    <strong>
                      {coordinateCustomer.customerNo ||
                        "—"}
                    </strong>
                  </div>

                  <div>
                    <span>
                      Sumber Aktif
                    </span>

                    <CoordinateSourceBadge
                      customer={
                        coordinateCustomer
                      }
                    />
                  </div>

                  <div>
                    <span>
                      Status Aktif
                    </span>

                    <CoordinateStatusBadge
                      latitude={
                        coordinateCustomer.latitude
                      }
                      longitude={
                        coordinateCustomer.longitude
                      }
                      status={
                        coordinateCustomer.coordinateStatus
                      }
                    />
                  </div>

                  <div>
                    <span>
                      Terakhir Diperbarui
                    </span>

                    <strong>
                      {formatTimestamp(
                        coordinateCustomer.coordinateVerifiedAt,
                      )}
                    </strong>
                  </div>
                </div>
              </section>

              <section className="master-form-group">
                <div className="coordinate-section-heading">
                  <div>
                    <h3>
                      Baseline Accurate
                    </h3>

                    <p>
                      Baseline terus diperbarui saat sinkronisasi,
                      termasuk ketika override manual sedang aktif.
                    </p>
                  </div>

                  <CoordinateStatusBadge
                    latitude={
                      coordinateCustomer.accurateLatitude
                    }
                    longitude={
                      coordinateCustomer.accurateLongitude
                    }
                    status={
                      coordinateCustomer.accurateCoordinateStatus ||
                      (coordinateCustomer.hasAccurateCoordinates
                        ? "VALID"
                        : "MISSING")
                    }
                  />
                </div>

                <div
                  className={[
                    "coordinate-accurate-baseline",
                    coordinateCustomer.hasAccurateCoordinateValues
                      ? "coordinate-accurate-baseline--available"
                      : "coordinate-accurate-baseline--missing",
                  ].join(" ")}
                >
                  <div>
                    <span>
                      Latitude Accurate
                    </span>

                    <strong>
                      {formatCoordinate(
                        coordinateCustomer.accurateLatitude,
                      )}
                    </strong>
                  </div>

                  <div>
                    <span>
                      Longitude Accurate
                    </span>

                    <strong>
                      {formatCoordinate(
                        coordinateCustomer.accurateLongitude,
                      )}
                    </strong>
                  </div>

                  <div>
                    <span>
                      Terakhir Diambil
                    </span>

                    <strong>
                      {formatTimestamp(
                        coordinateCustomer.accurateCoordinateSyncedAt,
                      )}
                    </strong>
                  </div>
                </div>

                {coordinateCustomer.accurateCoordinateStatus &&
                  coordinateCustomer.accurateCoordinateStatus !== "VALID" && (
                    <p className="coordinate-editor-note coordinate-editor-note--warning">
                      {coordinateStatusMessage(
                        coordinateCustomer.accurateCoordinateStatus,
                      )}
                    </p>
                  )}

                {!coordinateCustomer.hasAccurateCoordinateValues && (
                  <p className="coordinate-editor-note">
                    Baseline Accurate belum tersedia.
                    Jalankan <strong>Sinkronkan Pelanggan</strong> untuk
                    mengambil koordinat terbaru dari Accurate.
                  </p>
                )}
              </section>

              <section className="master-form-group">
                <div className="coordinate-section-heading">
                  <div>
                    <h3>
                      Override Manual
                    </h3>

                    <p>
                      Menyimpan form ini akan mengubah sumber aktif
                      menjadi Manual.
                    </p>
                  </div>
                </div>

                <div className="dialog-form__grid">
                  <label>
                    <span>Latitude</span>

                    <input
                      inputMode="decimal"
                      onChange={(event) =>
                        setCoordinateForm(
                          (current) => ({
                            ...current,
                            latitude:
                              event.target.value,
                          }),
                        )
                      }
                      placeholder="-6.123456"
                      value={
                        coordinateForm.latitude
                      }
                    />
                  </label>

                  <label>
                    <span>Longitude</span>

                    <input
                      inputMode="decimal"
                      onChange={(event) =>
                        setCoordinateForm(
                          (current) => ({
                            ...current,
                            longitude:
                              event.target.value,
                          }),
                        )
                      }
                      placeholder="110.123456"
                      value={
                        coordinateForm.longitude
                      }
                    />
                  </label>
                </div>

                <p className="coordinate-editor-note">
                  Koordinat manual langsung valid untuk operasional dan
                  tidak ditimpa saat pelanggan disinkronkan. Gunakan
                  <strong> Kembalikan ke Accurate</strong> untuk memakai
                  baseline Accurate terbaru.
                </p>
              </section>
            </div>

            <footer className="dialog-card__actions coordinate-dialog-actions">
              <div className="coordinate-dialog-actions__left">
                <button
                  className="danger-button--outline"
                  disabled={
                    Boolean(
                      coordinateAction,
                    )
                  }
                  onClick={
                    clearCoordinateForm
                  }
                  type="button"
                >
                  Kosongkan Koordinat
                </button>

                <button
                  className="secondary-button coordinate-restore-button"
                  disabled={
                    Boolean(
                      coordinateAction,
                    ) ||
                    !coordinateCustomer.canRestoreAccurate
                  }
                  onClick={
                    restoreAccurateCoordinates
                  }
                  title={
                    coordinateCustomer.hasAccurateCoordinates
                      ? "Gunakan kembali baseline Accurate terbaru"
                      : coordinateCustomer.hasAccurateCoordinateValues
                        ? "Baseline Accurate perlu diperbaiki sebelum dapat digunakan"
                        : "Sinkronkan pelanggan untuk mengambil baseline Accurate"
                  }
                  type="button"
                >
                  {coordinateAction ===
                  "restore"
                    ? "Mengembalikan…"
                    : "Kembalikan ke Accurate"}
                </button>
              </div>

              <button
                className="secondary-button"
                disabled={
                  Boolean(
                    coordinateAction,
                  )
                }
                onClick={
                  closeCoordinateEditor
                }
                type="button"
              >
                Batal
              </button>

              <button
                className="primary-button"
                disabled={
                  Boolean(
                    coordinateAction,
                  )
                }
                type="submit"
              >
                {coordinateAction ===
                "save"
                  ? "Menyimpan…"
                  : "Simpan Override"}
              </button>
            </footer>
          </form>
        </Dialog>
      )}
    </DataMasterSection>
  );
}
